import { useState, useEffect } from 'react'
import { PageHeader, SearchInput, Modal, EmptyState } from '../../components/UI'
import { get, put, post } from '../../utils/api'
import { formatDate, timeAgo } from '../../utils/helpers'
import { setTokens } from '../../utils/api'
import { IconUsers, IconPlus, IconEdit, IconUserShare } from '@tabler/icons-react'

const ROLES = ['admin', 'dos', 'sales_manager', 'sales_coordinator', 'front_desk']

export default function AdminUsers() {
  const [users, setUsers] = useState([])
  const [hotels, setHotels] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [hotelFilter, setHotelFilter] = useState('all')
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      const [u, h] = await Promise.all([get('/admin/users'), get('/admin/hotels')])
      setUsers(u); setHotels(h)
    } catch {} finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false
    if (hotelFilter !== 'all' && String(u.hotel_id) !== hotelFilter) return false
    if (!search) return true
    const s = search.toLowerCase()
    return u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s) || (u.hotel_name || '').toLowerCase().includes(s)
  })

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/admin/users', formData); setShowAdd(false); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const handleUpdate = async () => {
    setSaving(true)
    try { await put(`/admin/users/${editing.id}`, formData); setEditing(null); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const handleImpersonate = async (userId, userName) => {
    if (!confirm(`Log in as ${userName}? You'll be redirected to the CRM as this user.`)) return
    try {
      const data = await post(`/admin/impersonate/${userId}`)
      setTokens(data.accessToken, data.refreshToken)
      localStorage.setItem('leadiox-user', JSON.stringify(data.user))
      window.location.href = '/'
    } catch (err) { alert(err.message) }
  }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Users" subtitle={`${users.length} users across all hotels`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => { setFormData({}); setShowAdd(true) }}><IconPlus size={16} className="me-1" />Add User</button>} />

      {/* Stats */}
      <div className="row row-cards mb-3">
        {[{ l: 'Total', v: users.length }, { l: 'Active', v: users.filter(u => u.is_active).length },
          { l: 'Admins', v: users.filter(u => u.role === 'admin' && !u.is_platform_admin).length },
          { l: 'Platform Admins', v: users.filter(u => u.is_platform_admin).length }
        ].map(s => (
          <div key={s.l} className="col-6 col-lg-3"><div className="card stat-mini"><div className="card-body">
            <div className="subheader">{s.l}</div><div className="h3 mb-0">{s.v}</div>
          </div></div></div>
        ))}
      </div>

      {/* Filters */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search users..." />
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 200 }} value={hotelFilter} onChange={e => setHotelFilter(e.target.value)}>
          <option value="all">All Hotels</option>
          {hotels.map(h => <option key={h.id} value={String(h.id)}>{h.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={IconUsers} title="No users found" /> : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover">
              <thead><tr><th>User</th><th>Email</th><th>Hotel</th><th>Role</th><th>Active</th><th>Last Login</th><th>Actions</th></tr></thead>
              <tbody>{filtered.map(u => (
                <tr key={u.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span className={`avatar avatar-sm rounded-circle ${u.is_platform_admin ? 'bg-warning-lt' : 'bg-primary-lt'}`}>
                        {u.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                      <div>
                        <div className="fw-medium">{u.name}</div>
                        {u.is_platform_admin && <span className="badge bg-warning-lt" style={{ fontSize: '0.6rem' }}>PLATFORM ADMIN</span>}
                      </div>
                    </div>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.hotel_name || '---'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{(u.role || '').replace(/_/g, ' ')}</td>
                  <td><span className={`badge bg-${u.is_active ? 'green' : 'red'}-lt`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="text-secondary" style={{ fontSize: '0.85rem' }}>{u.last_login ? timeAgo(u.last_login) : 'Never'}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={() => { setEditing(u); setFormData({ ...u }) }} title="Edit"><IconEdit size={16} /></button>
                      {!u.is_platform_admin && <button className="btn btn-ghost-primary btn-icon btn-sm" onClick={() => handleImpersonate(u.id, u.name)} title="Login as this user"><IconUserShare size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add User" wide>
        <div className="row g-3">
          {[{ label: 'Name', key: 'name' }, { label: 'Email', key: 'email', type: 'email' },
            { label: 'Password', key: 'password', type: 'password' }, { label: 'Title', key: 'title' },
            { label: 'Phone', key: 'phone' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-md-6"><label className="form-label">Hotel</label>
            <select className="form-select" value={formData.hotel_id || ''} onChange={e => setFormData({ ...formData, hotel_id: parseInt(e.target.value) })}>
              <option value="">Select hotel...</option>{hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label">Role</label>
            <select className="form-select" value={formData.role || ''} onChange={e => setFormData({ ...formData, role: e.target.value })}>
              <option value="">Select...</option>{ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}</select></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>{saving ? 'Creating...' : 'Add User'}</button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit: ${editing?.name || ''}`} wide>
        <div className="row g-3">
          {[{ label: 'Name', key: 'name' }, { label: 'Email', key: 'email' }, { label: 'Title', key: 'title' }, { label: 'Phone', key: 'phone' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-md-6"><label className="form-label">Hotel</label>
            <select className="form-select" value={formData.hotel_id || ''} onChange={e => setFormData({ ...formData, hotel_id: parseInt(e.target.value) })}>
              {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label">Role</label>
            <select className="form-select" value={formData.role || ''} onChange={e => setFormData({ ...formData, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}</select></div>
          <div className="col-md-6"><label className="form-label">Active</label>
            <select className="form-select" value={formData.is_active === false ? 'false' : 'true'} onChange={e => setFormData({ ...formData, is_active: e.target.value === 'true' })}>
              <option value="true">Active</option><option value="false">Inactive</option></select></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleUpdate}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </Modal>
    </>
  )
}
